import {
  PPQ,
  PROJECT_FORMAT_VERSION,
  type AudioClip,
  type ClipFade,
  type EffectInstance,
  type InstrumentState,
  type MidiClip,
  type Project,
  type Track,
  type TrackKind,
} from './types';
import { newClipId, newProjectId, newTrackId } from '@/util/id';

export const APP_VERSION = '0.1.0';

export const TRACK_COLORS = [
  '#5b8def',
  '#3fb984',
  '#e0a13c',
  '#d4635f',
  '#9b6fd4',
  '#3fa9b9',
  '#c9748f',
  '#7f9a4e',
] as const;

export const DEFAULT_TRACK_HEIGHT = 96;
export const MIN_TRACK_HEIGHT = 44;
export const MAX_TRACK_HEIGHT = 420;
/**
 * Playback-rate bounds. Wider than the pitch-preserving stretcher's own range,
 * because varispeed has no such limit; a stretched clip is clamped further by
 * MIN_STRETCH_RATIO / MAX_STRETCH_RATIO.
 */
export const MIN_PLAYBACK_RATE = 0.05;
export const MAX_PLAYBACK_RATE = 8;
/**
 * Transposition bounds, in semitones. Two octaves either way is already past
 * where the shifter holds a recognisable timbre; the limit is here rather than
 * in the engine so the domain can validate a loaded project without knowing how
 * the shift is implemented.
 */
export const MIN_PITCH_SEMITONES = -24;
export const MAX_PITCH_SEMITONES = 24;
/** Bounds for the vertical zoom multiplier applied to every lane. */
export const MIN_TRACK_HEIGHT_SCALE = 0.5;
export const MAX_TRACK_HEIGHT_SCALE = 2.5;

export const MASTER_TRACK_ID = 'trk_master';

export function noFade(): ClipFade {
  return { durationSec: 0, curve: 'equalPower', curvature: 0 };
}

export function createTrack(kind: TrackKind, name: string, index = 0, id?: string): Track {
  return {
    id: id ?? newTrackId(),
    kind,
    name,
    color: TRACK_COLORS[index % TRACK_COLORS.length],
    height: DEFAULT_TRACK_HEIGHT,
    volume: 1,
    pan: 0,
    mute: false,
    solo: false,
    armed: false,
    inputMonitor: false,
    inputDeviceId: null,
    outputTrackId: kind === 'master' ? null : MASTER_TRACK_ID,
    sends: [],
    effects: [],
    instrument: null,
    clipIds: [],
    automationLaneIds: [],
    collapsed: false,
    automationMode: 'read',
    showAutomation: false,
    showTakeLanes: false,
    takeNames: {},
    freeze: null,
  };
}

export function createMasterTrack(): Track {
  const t = createTrack('master', 'Master', 0, MASTER_TRACK_ID);
  t.color = '#8a949e';
  t.height = DEFAULT_TRACK_HEIGHT;
  return t;
}

export function createAudioClip(params: {
  trackId: string;
  assetId: string;
  name: string;
  startTicks: number;
  offsetSec: number;
  durationSec: number;
  id?: string;
}): AudioClip {
  return {
    id: params.id ?? newClipId(),
    kind: 'audio',
    gainEnvelope: [],
    trackId: params.trackId,
    name: params.name,
    startTicks: Math.max(0, Math.round(params.startTicks)),
    muted: false,
    color: null,
    takeLane: 0,
    assetId: params.assetId,
    offsetSec: Math.max(0, params.offsetSec),
    durationSec: Math.max(0, params.durationSec),
    gain: 1,
    fadeIn: noFade(),
    fadeOut: noFade(),
    playbackRate: 1,
    preservePitch: true,
    warpSourceBpm: null,
    warpMarkers: [],
    reversed: false,
    pitchSemitones: 0,
    formantSemitones: null,
  };
}

export function createMidiClip(params: {
  trackId: string;
  name: string;
  startTicks: number;
  durationTicks: number;
  id?: string;
}): MidiClip {
  return {
    id: params.id ?? newClipId(),
    kind: 'midi',
    trackId: params.trackId,
    name: params.name,
    startTicks: Math.max(0, Math.round(params.startTicks)),
    muted: false,
    color: null,
    takeLane: 0,
    durationTicks: Math.max(1, Math.round(params.durationTicks)),
    notes: [],
    loop: false,
    loopLengthTicks: Math.max(1, Math.round(params.durationTicks)),
  };
}

/** A brand-new, empty project with a master bus and two audio tracks. */
export function createEmptyProject(name = 'Untitled Project'): Project {
  const now = Date.now();
  const master = createMasterTrack();
  const a = createTrack('audio', 'Audio 1', 0);
  const b = createTrack('audio', 'Audio 2', 1);

  return {
    meta: {
      id: newProjectId(),
      name,
      createdAt: now,
      modifiedAt: now,
      formatVersion: PROJECT_FORMAT_VERSION,
      appVersion: APP_VERSION,
    },
    transport: {
      ppq: PPQ,
      tempoMap: [{ tick: 0, bpm: 120 }],
      timeSignatures: [{ tick: 0, numerator: 4, denominator: 4 }],
      loopEnabled: false,
      loopStartTicks: 0,
      loopEndTicks: PPQ * 16,
      metronomeEnabled: false,
      countInBars: 1,
      snap: '1/4',
      crossfadeSec: 0.01,
      playheadTicks: 0,
    },
    tracks: { [master.id]: master, [a.id]: a, [b.id]: b },
    trackOrder: [a.id, b.id],
    masterTrackId: master.id,
    clips: {},
    automation: {},
    assets: {},
    markers: [],
    view: {
      pixelsPerBeat: 32,
      scrollTicks: 0,
      scrollTop: 0,
      trackHeightScale: 1,
      bottomPanel: 'none',
    },
  };
}

export function createEffect(type: EffectInstance['type'], params: Record<string, number>, id: string): EffectInstance {
  return { id, type, enabled: true, params: { ...params } };
}

export function createInstrument(type: InstrumentState['type'], params: Record<string, number>): InstrumentState {
  return { type, params: { ...params }, data: {} };
}
